import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import BrandMark from '../BrandMark';

// Navigation links for the landing page
const navigationLinks = [
  { id: 'how-it-works', label: 'How It Works', href: '#how-it-works', type: 'scroll' },
  { id: 'features', label: 'Features', href: '#features', type: 'scroll' },
  { id: 'faq', label: 'FAQ', href: '#faq', type: 'faq' },
  { id: 'knowledge', label: 'Knowledge', href: '/knowledge', type: 'route' },
];

const Navbar = ({ onLoginClick, onContactClick, onFAQClick, onClientPortalClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    if (!isHomePage) {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavLink = (link) => {
    if (link.type === 'faq') {
      if (onFAQClick) onFAQClick();
      setIsMobileMenuOpen(false);
    } else {
      scrollToSection(link.href);
    }
  };

  const handleContactClick = () => {
    if (onContactClick) {
      onContactClick();
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop: 3-column grid for perfect logo centering */}
        <div className="hidden md:grid grid-cols-3 items-center h-20">
          {/* Left Navigation */}
          <div className="flex items-center space-x-6 lg:space-x-8">
            {navigationLinks.map((link) => {
              // Internal SPA route (uses React Router Link)
              if (link.type === 'route') {
                return (
                  <Link
                    key={link.id}
                    to={link.href}
                    data-testid={`navbar-${link.id}-link`}
                    className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-sm font-medium tracking-wide whitespace-nowrap"
                  >
                    {link.label}
                  </Link>
                );
              }
              // For scroll links on non-home pages, use native <a> for reliable hash navigation
              if (link.type === 'scroll' && !isHomePage) {
                return (
                  <a
                    key={link.id}
                    href={'/' + link.href}
                    data-testid={`navbar-${link.id}-link`}
                    className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-sm font-medium tracking-wide whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <button
                  key={link.id}
                  onClick={() => handleNavLink(link)}
                  data-testid={link.id === 'faq' ? 'navbar-faq-btn' : `navbar-${link.id}-btn`}
                  className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-sm font-medium tracking-wide whitespace-nowrap"
                >
                  {link.label}
                </button>
              );
            })}
          </div>

          {/* Center Logo */}
          <div className="flex items-center justify-center">
            <a href="/" className="flex items-center space-x-2 group">
              <BrandMark size="md" textSize="text-2xl" />
            </a>
          </div>

          {/* Right Navigation */}
          <div className="flex items-center justify-end space-x-5 lg:space-x-6">
            <button
              onClick={handleContactClick}
              className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-sm font-medium tracking-wide whitespace-nowrap"
              data-testid="navbar-contact-btn"
            >
              Partner with Us
            </button>
            <Link
              to="/about"
              className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-sm font-medium tracking-wide whitespace-nowrap"
            >
              About Us
            </Link>
            {onClientPortalClick && (
              <button
                onClick={onClientPortalClick}
                className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="navbar-client-portal-btn"
              >
                Client Portal
              </button>
            )}
            <Button
              onClick={onLoginClick}
              variant="outline"
              className="border-amber-400/50 text-amber-400 hover:bg-amber-400/10 hover:border-amber-400 transition-all duration-200 whitespace-nowrap"
              data-testid="navbar-partner-login-btn"
            >
              Partner Login
            </Button>
          </div>
        </div>

        {/* Mobile: logo left, hamburger right */}
        <div className="md:hidden flex items-center justify-between h-20">
          <a href="/" className="flex items-center space-x-2">
            <BrandMark size="md" textSize="text-2xl" />
          </a>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-slate-300 hover:text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-md rounded-b-2xl pb-6 animate-in slide-in-from-top duration-200">
            <div className="flex flex-col space-y-4 px-4">
              {navigationLinks.map((link) => {
                if (link.type === 'route') {
                  return (
                    <Link
                      key={link.id}
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-navbar-${link.id}-link`}
                      className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-left py-2 border-b border-slate-800"
                    >
                      {link.label}
                    </Link>
                  );
                }
                if (link.type === 'scroll' && !isHomePage) {
                  return (
                    <a
                      key={link.id}
                      href={'/' + link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-left py-2 border-b border-slate-800"
                    >
                      {link.label}
                    </a>
                  );
                }
                return (
                  <button
                    key={link.id}
                    onClick={() => handleNavLink(link)}
                    className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-left py-2 border-b border-slate-800"
                  >
                    {link.label}
                  </button>
                );
              })}
              <button
                onClick={handleContactClick}
                className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-left py-2 border-b border-slate-800"
              >
                Partner with Us
              </button>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-300 hover:text-amber-400 transition-colors duration-200 text-left py-2 border-b border-slate-800"
              >
                About Us
              </Link>
              {onClientPortalClick && (
                <Button
                  onClick={() => {
                    onClientPortalClick();
                    setIsMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="border-amber-400/50 text-amber-400 hover:bg-amber-400/10 w-full mt-2"
                  data-testid="mobile-client-portal-btn"
                >
                  Client Portal
                </Button>
              )}
              <Button
                onClick={() => {
                  onLoginClick();
                  setIsMobileMenuOpen(false);
                }}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold w-full"
                data-testid="mobile-partner-login-btn"
              >
                Partner Login
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
